import plural from 'plurals-cldr'
import {LangUtil, PluralNoun} from '../lang-util'

const countable = (n: PluralNoun): PluralNoun => n
const numEn = (key: keyof typeof repeatEn, n: number): string =>
  (repeatEn[key] as PluralNoun)[plural('en', n)] ?? ''
const numRu = (key: keyof typeof repeatEn, n: number): string =>
  (repeatRu[key] as PluralNoun)[plural('ru', n)] ?? ''

export const repeatEn = {
  stickerName: 'Regular task',
  stickerTitle: 'Sticker “Regular task”',
  stickerDescription: LangUtil.stripMargin(`
    if this sticker is added to a task then
    it will appear on the board regularly
  `),
  example: 'Example: weekly planning meeting',
  repeatSettings: 'Repeat settings',
  repeatEvery: 'Repeat interval',
  repeatEveryMinute: 'every minute',
  repeatEveryHour: 'every hour',
  repeatTill: 'Repeat till',
  start: 'Start',
  stop: 'Stop',
  repeatLocation: 'Repeated task location',
  suffixTitle: 'Add to title',
  suffixDate: 'Creation date',
  suffixOrder: 'Number of repetition',
  nextRepeat: 'Next repeat',
  unpin: 'Unpin this sticker from created repeated tasks and stop repetitions?',
  repeatedTitle: 'Repeats of the task',
  permissionWarn:
    'Only users with role “Manager” or “Worker” in this project ' +
    'and company administrators can modify repeat tasks',
  createTemplateFromTask: 'Create regular task based on this task?',
  boardEditTitle: (title: string) =>
    `Repeated task templates in board “${title}”`,
  taskEditTitle: 'Regular task template',
  regularTasks: countable({
    one: 'regular task template',
    other: 'regular task templates'
  }),
  noTasks: 'There are no regular tasks in this board',
  someTasks1: `There are`,
  someTasks2: (n: number) => `${numEn('regularTasks', n)} in this board`,
  create: 'Create regular tasks',
  edit: 'Edit regular tasks',
  newTemplate: 'Create regular task template',
  backToTemplates: 'Back to regular tasks templates',
  tableName: 'Task name',
  tableLoc: 'Location',
  tableConditions: 'Repeat conditions',
  tableActions: 'Actions',
  actionEdit: 'Edit',
  actionStop: 'Stop',
  actionStart: 'Start',
  actionDelete: 'Delete',
  scheduleDaily: (time: string, each = 1) =>
    `${each === 1 ? 'Daily' : `Each ${each} day`} at ${time}`,
  scheduleWeekly: (day: string, time: string, each = 1) =>
    `${each === 1 ? 'Weekly' : `Each ${each} week`} on ${day} at ${time}`,
  scheduleMonthly: (daysOfMonth: string, time: string, each = 1) =>
    `${
      each === 1 ? 'Monthly' : `Each ${each} month`
    } at ${daysOfMonth} at ${time}`,
  scheduleMonthlyWithSelectedDay: (
    selectedDay: string,
    time: string,
    each = 1
  ) =>
    `${
      each === 1 ? 'Monthly' : `Each ${each} month`
    }, ${selectedDay} at ${time}`,
  scheduleYearly: (
    months: string,
    daysOfMonth: string,
    time: string,
    each = 1
  ) =>
    `${
      each === 1 ? 'Yearly' : `Each ${each} year`
    } at ${daysOfMonth} of ${months} at ${time}`,
  scheduleYearlyWithSelectedDay: (
    months: string,
    selectedDay: string,
    time: string,
    each = 1
  ) =>
    `${
      each === 1 ? 'Yearly' : `Each ${each} year`
    }, ${selectedDay} of ${months} at ${time}`,
  namePlaceholder: 'Enter regular task name',
  youCanEditTemplate:
    'You can edit regular contents of the automatically created tasks in right panel',
  youCanEditTemplate1: 'description',
  youCanEditTemplate2: 'checklists',
  youCanEditTemplate3: 'assigned users',
  youCanEditTemplate4: 'deadline',
  youCanEditTemplate5: 'stickers',
  changesSaved: 'Changes are saved',
  editTemplate: 'Open template',
  noColumnsWarn:
    'This board has no columns available for regular task creation',
  deleteWarn: 'Delete this regular task and stop repeating this task?',
  stopped: 'Stopped',
  templateHeader: 'Regular task template',
  templateTaskInfo: 'Regular task template',
  taskName: 'Task name',
  repeatNumber: 'Regular task repeat',
  showRepeatTasks: 'Show repeated tasks settings in this board',
  emptyNameError: 'Repeat task name should‘t be empty',
  canEditWarn:
    'Only users with role “Manager” or “Worker” in this project ' +
    'can edit regular tasks',
  confirmClose: 'You have unsaved changes. Close this window without saving?',
  confirmBack: 'You have unsaved changes. Go back without saving?',
  atTime: (time: string) => `at ${time}`,
  today: 'Today',
  tomorrow: 'Tomorrow',
  taskWillAppear: 'Task will appear on board',
  willAppear: 'Will appear on board',
  nearestRepeat: 'Nearest repetition',
  date: 'Creation date',
  openTemplate: 'Open template',
  tplCreateTo: 'Tasks are created in column',
  templateDeleted: 'Template was deleted',
  deadlineHelp:
    'Deadline is for the nearest repeat. ' +
    'It will be shifted automatically after repeat',
  explain0Repeat:
    'Repeat template was created based on this task. ' +
    'Change template to modify future repeated tasks.'
}

export const repeatRu: typeof repeatEn = {
  stickerName: 'Регулярная задача',
  stickerTitle: 'Стикер «Регулярная задача»',
  stickerDescription: LangUtil.stripMargin(`
    если добавить этот стикер на задачу, то она будет появляться на
    доске регулярно. Можно настроить периодичность повторений
  `),
  example: 'Пример: еженедельное планирование',
  repeatSettings: 'Настройки повторения',
  repeatEvery: 'Интервал повторения',
  repeatEveryMinute: 'каждую минуту',
  repeatEveryHour: 'каждый час',
  repeatTill: 'Повторять до',
  start: 'Начать повторять',
  repeatLocation: 'Расположение задачи',
  stop: 'Отключить повторение',
  suffixTitle: 'Добавлять к названию',
  suffixDate: 'Дату повторения',
  suffixOrder: 'Порядковый номер',
  nextRepeat: 'Следующее повторение',
  unpin:
    'Открепить этот стикер со всех созданных задач и остановить повторения?',
  repeatedTitle: 'Повторения задачи',
  permissionWarn:
    'Только пользователи с ролью в проекте “Управляющий” или “Сотрудник” ' +
    'и администраторы компании могут менять регулярные задачи',
  createTemplateFromTask: 'Создать регулярную задачу на основе этой задачи?',
  boardEditTitle: (title: string) =>
    `Шаблоны регулярных задач в доске “${title}”`,
  taskEditTitle: 'Шаблон регулярной задачи',
  regularTasks: countable({
    one: 'шаблон регулярной задачи',
    few: 'шаблона регулярных задач',
    many: 'шаблонов регулярных задач'
  }),
  noTasks: 'На этой доске пока нет регулярных задач',
  someTasks1: `На этой доске`,
  someTasks2: (n: number) => `${numRu('regularTasks', n)}`,
  create: 'Создать регулярные задачи',
  edit: 'Настройка регулярных задач',
  newTemplate: 'Создать шаблон регулярной задачи',
  backToTemplates: 'Вернуться к списку шаблонов',
  tableName: 'Название задачи',
  tableLoc: 'Расположение',
  tableConditions: 'Условия повторения',
  tableActions: 'Действия',
  actionEdit: 'Редактировать',
  actionStop: 'Остановить',
  actionStart: 'Запустить',
  actionDelete: 'Удалить',
  scheduleDaily: (time: string, each = 1) =>
    `${each === 1 ? 'Ежедневно' : `Каждый ${each}й день`} в ${time}`,
  scheduleWeekly: (day: string, time: string, each = 1) =>
    `${each === 1 ? 'Еженедельно' : `Каждую ${each}ю неделю`} в ${day} ${time}`,
  scheduleMonthly: (daysOfMonth: string, time: string, each = 1) =>
    `${
      each === 1 ? 'Ежемесячно' : `Каждый ${each}й месяц`
    } ${daysOfMonth} числа, в ${time}`,
  scheduleMonthlyWithSelectedDay: (
    selectedDay: string,
    time: string,
    each = 1
  ) =>
    `${
      each === 1 ? 'Ежемесячно' : `Каждый ${each}й месяц`
    }, ${selectedDay}, в ${time}`,
  scheduleYearly: (
    months: string,
    daysOfMonth: string,
    time: string,
    each = 1
  ) =>
    `${
      each === 1 ? 'Ежегодно' : `Каждый ${each}й год`
    } ${daysOfMonth} числа ${months}, в ${time}`,
  scheduleYearlyWithSelectedDay: (
    months: string,
    selectedDay: string,
    time: string,
    each = 1
  ) =>
    `${
      each === 1 ? 'Ежегодно' : `Каждый ${each}й год`
    }, ${selectedDay} ${months}, в ${time}`,
  namePlaceholder: 'Введите название регулярной задачи',
  youCanEditTemplate:
    'Вы можете изменить параметры задач, ' +
    'которые будут создаваться по этому шаблону в правой панели',
  youCanEditTemplate1: 'описание',
  youCanEditTemplate2: 'подзадачи',
  youCanEditTemplate3: 'исполнителей',
  youCanEditTemplate4: 'дедлайн',
  youCanEditTemplate5: 'стикеры',
  changesSaved: 'Изменения сохранены',
  editTemplate: 'Открыть шаблон',
  noColumnsWarn:
    'В этой доске нет ни одной колонки, в которую можно было бы ' +
    'добавлять регулярную задачу. Создайте обычную колонку в доске, ' +
    'чтобы добавлять регулярные задачи',
  deleteWarn: 'Удалить эту регулярную задачу и остановить повторение задачи?',
  stopped: 'Остановлено',
  templateHeader: 'Шаблон регулярной задачи',
  templateTaskInfo: 'Шаблон регулярной задачи',
  taskName: 'Название задачи',
  repeatNumber: 'Повторение регулярной задачи',
  showRepeatTasks: 'Показать настройки регулярных задач в доске',
  emptyNameError: 'Название задачи не должно быть пустым',
  canEditWarn:
    'Только пользователи с ролью “Управляющий” или “Сотрудник” в этом ' +
    'проекте могут редактировать регулярные задачи в этой доске',
  confirmClose:
    'У вас есть несохранённые изменения. Закрыть это окно без сохранения?',
  confirmBack:
    'У вас есть несохранённые изменения. Перейти в список без сохранения?',
  atTime: (time: string) => `в ${time}`,
  today: 'Сегодня',
  tomorrow: 'Завтра',
  taskWillAppear: 'Ближайшие повторения',
  willAppear: 'Появится на доске',
  nearestRepeat: 'Ближайшее повторение',
  date: 'Дата повторения',
  openTemplate: 'Открыть шаблон',
  tplCreateTo: 'Задачи создаются в колонке',
  templateDeleted: 'Шаблон, по которому создана эта задача был удалён',
  deadlineHelp:
    'Дедлайн указан для ближайшего повторения. ' +
    'После повторения он автоматически сместится',
  explain0Repeat:
    'На основе этой задачи был создан шаблон повторяющейся задачи. ' +
    'Чтобы изменить параметры задач, которые будут повторяться, ' +
    'отредактируйте шаблон.'
}
